if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mql.addEventListener('change', this.syncViewerControls.bind(this));

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.bindViewerButtons();
          this.bindGalleryDots();
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('button');
            if (!button) return;
            button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          this.bindThumbnailDragScroll();
          this.bindThumbnailHeightSync();
        }

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        this.syncViewerControls();
      }

      connectedCallback() {
        if (!this.closest('quick-add-modal')) return;
        this.bindViewerDrag();
        this.syncViewerControls();
      }

      get isInsideQuickAdd() {
        return Boolean(this.closest('quick-add-modal'));
      }

      bindThumbnailHeightSync() {
        if (!this.elements.thumbnails || !this.elements.viewer) return;

        this.syncThumbnailHeight = this.syncThumbnailHeight.bind(this);
        this.mql.addEventListener('change', this.syncThumbnailHeight);

        // Sync after layout / images settle
        requestAnimationFrame(() => this.syncThumbnailHeight());
        window.addEventListener('resize', this.syncThumbnailHeight);
        this.elements.viewer.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this.syncThumbnailHeight, { once: true });
        });

        if (typeof ResizeObserver !== 'undefined') {
          this.thumbnailHeightObserver = new ResizeObserver(() => this.syncThumbnailHeight());
          this.thumbnailHeightObserver.observe(this.elements.viewer);
          const media = this.elements.viewer.querySelector('.product-media-container, .product__media-item.is-active');
          if (media) this.thumbnailHeightObserver.observe(media);
        }
      }

      syncThumbnailHeight() {
        const slider = this.elements.thumbnails;
        const list = slider?.querySelector('.thumbnail-list');
        if (!slider || !list) return;

        if (!this.mql.matches) {
          slider.style.height = '';
          slider.style.maxHeight = '';
          slider.style.minHeight = '';
          list.style.height = '';
          list.style.maxHeight = '';
          list.style.removeProperty('--thumbnail-item-height');
          return;
        }

        // Match the main image frame — size thumbs so exactly 5.3 are visible
        const VISIBLE_THUMBS = 5.3;
        const activeMedia =
          this.elements.viewer.querySelector('.product__media-item.is-active .product-media-container') ||
          this.elements.viewer.querySelector('.product__media-item.is-active .product__modal-opener') ||
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('.product-media-container');

        const targetHeight = activeMedia?.getBoundingClientRect().height;
        if (!targetHeight || targetHeight < 1) return;

        const gap = parseFloat(getComputedStyle(list).rowGap || getComputedStyle(list).gap) || 16;
        const gapCount = Math.floor(VISIBLE_THUMBS); // 5 gaps around 5.3 visible items
        const itemHeight = Math.max(1, (targetHeight - gapCount * gap) / VISIBLE_THUMBS);

        const heightPx = `${Math.round(targetHeight)}px`;
        slider.style.height = heightPx;
        slider.style.maxHeight = heightPx;
        slider.style.minHeight = heightPx;
        list.style.height = heightPx;
        list.style.maxHeight = heightPx;
        list.style.setProperty('--thumbnail-item-height', `${itemHeight}px`);
      }

      bindThumbnailDragScroll() {
        const list = this.elements.thumbnails?.querySelector('.thumbnail-list');
        if (!list || list.dataset.dragScrollBound === 'true') return;
        list.dataset.dragScrollBound = 'true';

        const DRAG_THRESHOLD = 4;
        let isPointerDown = false;
        let isDragging = false;
        let suppressClick = false;
        let startY = 0;
        let startScrollTop = 0;
        let activePointerId = null;

        const endDrag = (event) => {
          if (!isPointerDown) return;
          if (activePointerId != null && event?.pointerId != null && event.pointerId !== activePointerId) return;

          if (isDragging && activePointerId != null && list.hasPointerCapture?.(activePointerId)) {
            try {
              list.releasePointerCapture(activePointerId);
            } catch (e) {
              /* ignore */
            }
          }

          if (isDragging) {
            suppressClick = true;
            // Fallback if no click event follows the drag
            setTimeout(() => {
              suppressClick = false;
            }, 50);
          }

          isPointerDown = false;
          isDragging = false;
          activePointerId = null;
          list.classList.remove('is-dragging');
        };

        list.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          if (!this.mql.matches) return;
          if (list.scrollHeight <= list.clientHeight) return;

          isPointerDown = true;
          isDragging = false;
          activePointerId = event.pointerId;
          startY = event.clientY;
          startScrollTop = list.scrollTop;
        });

        list.addEventListener('pointermove', (event) => {
          if (!isPointerDown || activePointerId !== event.pointerId) return;

          const deltaY = event.clientY - startY;
          if (!isDragging && Math.abs(deltaY) < DRAG_THRESHOLD) return;

          if (!isDragging) {
            isDragging = true;
            list.classList.add('is-dragging');
            try {
              list.setPointerCapture(event.pointerId);
            } catch (e) {
              /* ignore */
            }
          }

          event.preventDefault();
          list.scrollTop = startScrollTop - deltaY;
        });

        list.addEventListener('pointerup', endDrag);
        list.addEventListener('pointercancel', endDrag);
        list.addEventListener('lostpointercapture', endDrag);

        // Prevent accidental click after a drag
        list.addEventListener(
          'click',
          (event) => {
            if (!suppressClick) return;
            event.preventDefault();
            event.stopPropagation();
            suppressClick = false;
          },
          true
        );
      }

      get mediaItems() {
        if (!this.elements.viewer) return [];
        // Only real gallery slides — nested nodes can also carry data-media-id
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      get usesActiveSlideNavigation() {
        if (this.isInsideQuickAdd) return this.mediaItems.length > 1;
        return Boolean(this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches);
      }

      bindViewerDrag() {
        const slider = this.elements.viewer?.querySelector('[id^="Slider-"]');
        if (!slider || slider.dataset.quickAddDragBound === 'true') return;
        if (this.mediaItems.length < 2) return;

        slider.dataset.quickAddDragBound = 'true';

        const DRAG_THRESHOLD = 40;
        let isPointerDown = false;
        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let activePointerId = null;
        let suppressClick = false;

        const isNavControl = (target) =>
          target.closest(
            '.slider-buttons, .product-gallery-dots, .slider-button, .thumbnail-slider, .product__media-toggle, modal-opener.product__modal-opener'
          );

        const goBy = (direction) => {
          const items = this.mediaItems;
          if (items.length < 2) return;

          const activeIndex = Math.max(
            0,
            items.findIndex((item) => item.classList.contains('is-active'))
          );
          let nextIndex = activeIndex + direction;
          if (nextIndex < 0) nextIndex = items.length - 1;
          if (nextIndex >= items.length) nextIndex = 0;

          this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
        };

        const endDrag = (event) => {
          if (!isPointerDown) return;
          if (activePointerId != null && event?.pointerId != null && event.pointerId !== activePointerId) return;

          const wasDragging = isDragging;
          const deltaX = (event?.clientX ?? startX) - startX;
          const pointerId = activePointerId;

          isPointerDown = false;
          isDragging = false;
          activePointerId = null;
          slider.classList.remove('is-dragging');

          if (wasDragging && pointerId != null && slider.hasPointerCapture?.(pointerId)) {
            try {
              slider.releasePointerCapture(pointerId);
            } catch (e) {
              /* ignore */
            }
          }

          if (!wasDragging) return;

          if (Math.abs(deltaX) >= DRAG_THRESHOLD) {
            goBy(deltaX < 0 ? 1 : -1);
          }
          suppressClick = true;
          setTimeout(() => {
            suppressClick = false;
          }, 50);
        };

        slider.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          if (isNavControl(event.target)) return;
          if (this.mediaItems.length < 2) return;

          isPointerDown = true;
          isDragging = false;
          activePointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
        });

        slider.addEventListener(
          'pointermove',
          (event) => {
            if (!isPointerDown || activePointerId !== event.pointerId) return;

            const deltaX = event.clientX - startX;
            const deltaY = event.clientY - startY;

            if (!isDragging) {
              if (Math.abs(deltaX) < DRAG_THRESHOLD && Math.abs(deltaY) < DRAG_THRESHOLD) return;
              if (Math.abs(deltaY) > Math.abs(deltaX)) {
                isPointerDown = false;
                return;
              }

              isDragging = true;
              slider.classList.add('is-dragging');
              try {
                slider.setPointerCapture(event.pointerId);
              } catch (e) {
                /* ignore */
              }
            }

            event.preventDefault();
          },
          { passive: false }
        );

        slider.addEventListener('pointerup', endDrag);
        slider.addEventListener('pointercancel', endDrag);
        slider.addEventListener('lostpointercapture', endDrag);

        slider.addEventListener('dragstart', (event) => event.preventDefault());

        slider.addEventListener(
          'click',
          (event) => {
            if (!suppressClick) return;
            event.preventDefault();
            event.stopPropagation();
            suppressClick = false;
          },
          true
        );
      }

      bindViewerButtons() {
        this.prevButton = this.elements.viewer.querySelector('button[name="previous"]');
        this.nextButton = this.elements.viewer.querySelector('button[name="next"]');

        // Capture phase so we can override SliderComponent scroll behavior on desktop
        this.prevButton?.addEventListener('click', (event) => this.onViewerButtonClick(event, -1), true);
        this.nextButton?.addEventListener('click', (event) => this.onViewerButtonClick(event, 1), true);
      }

      onViewerButtonClick(event, direction) {
        if (!this.usesActiveSlideNavigation) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const items = this.mediaItems;
        if (items.length < 2) return;

        const activeIndex = Math.max(
          0,
          items.findIndex((item) => item.classList.contains('is-active'))
        );
        let nextIndex = activeIndex + direction;
        if (nextIndex < 0) nextIndex = items.length - 1;
        if (nextIndex >= items.length) nextIndex = 0;

        this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      }

      syncViewerControls() {
        if (!this.prevButton && !this.nextButton) return;

        if (this.usesActiveSlideNavigation) {
          // Desktop thumbnail layout uses is-active switching, not scroll — keep arrows enabled + same look
          [this.prevButton, this.nextButton].forEach((button) => {
            if (!button) return;
            button.removeAttribute('disabled');
            button.setAttribute('aria-disabled', 'false');
          });

          if (this.elements.viewer && !this.elements.viewer.__galleryNavPatched) {
            this.elements.viewer.__galleryNavPatched = true;
            this.elements.viewer.update = () => {
              [this.prevButton, this.nextButton].forEach((button) => {
                if (!button) return;
                button.removeAttribute('disabled');
                button.setAttribute('aria-disabled', 'false');
              });
            };
            // SliderComponent may disable arrows after construct — clear again on next frame
            requestAnimationFrame(() => this.elements.viewer.update());
          }
        }
      }

      onSlideChanged(event) {
        this.syncGalleryDots(event?.detail?.currentPage);

        if (!this.elements.thumbnails || !event?.detail?.currentElement) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      syncGalleryDots(currentPage) {
        const dots = this.elements.viewer?.querySelectorAll('.product-gallery-dots__dot');
        if (!dots?.length || !currentPage) return;

        dots.forEach((dot) => {
          const isActive = Number(dot.dataset.dot) === Number(currentPage);
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      bindGalleryDots() {
        const dots = this.elements.viewer?.querySelectorAll('.product-gallery-dots__dot');
        if (!dots?.length) return;

        dots.forEach((dot) => {
          dot.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const index = Number(dot.dataset.dot) - 1;
            const items = this.mediaItems;
            if (index < 0 || !items[index]) return;

            this.setActiveMedia(items[index].dataset.mediaId, false);
          });
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }

        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        // Prevent scroll-trigger fade leaving the newly shown slide invisible
        activeMedia.classList.add('scroll-trigger--cancel');
        activeMedia.style.opacity = '1';
        activeMedia.style.transform = 'none';

        // Eager-load images that were hidden (lazy) so the slide isn't blank/black
        activeMedia.querySelectorAll('img').forEach((img) => {
          if (img.getAttribute('loading') === 'lazy') {
            img.setAttribute('loading', 'eager');
          }
          if (img.dataset.src && !img.getAttribute('src')) {
            img.setAttribute('src', img.dataset.src);
          }
          // Force decode/paint for previously display:none slides
          if (typeof img.decode === 'function') {
            img.decode().catch(() => {});
          }
        });

        if (prepend) {
          if (activeMedia.parentElement?.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement?.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);
        this.syncViewerControls();

        // Mobile scroll gallery: jump viewer to the selected media
        if (!this.mql.matches && this.elements.viewer?.slider && activeMedia) {
          this.elements.viewer.slider.scrollTo({
            left: activeMedia.offsetLeft,
          });
        }

        const activeIndex = this.mediaItems.findIndex((item) => item === activeMedia);
        if (activeIndex >= 0) {
          this.syncGalleryDots(activeIndex + 1);
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        this.syncThumbnailHeight?.();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
            top: thumbnail.offsetTop,
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer?.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems?.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
